import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (!size) return '';
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const statusStyles = {
  queued: 'border-cyan-600/50 bg-cyan-950/20 text-cyan-300',
  processing: 'border-blue-600/50 bg-blue-950/20 text-blue-300',
  completed: 'border-green-600/50 bg-green-950/20 text-green-300',
  failed: 'border-red-600/50 bg-red-950/20 text-red-300',
  canceled: 'border-gray-600 bg-gray-800 text-gray-300',
};

const ExportPanel = ({
  beatInfo,
  channels,
  onBounce,
  onQueueCloudExport,
  cloudExportJobs = [],
  cloudExportsLoading = false,
  onRefreshCloudExports,
  onCancelCloudExport,
  disabled,
}) => {
  const [format, setFormat] = useState('wav24');
  const [cloudFormat, setCloudFormat] = useState('wav24');
  const [sampleRate, setSampleRate] = useState('44100');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [stemExporting, setStemExporting] = useState(null);
  const [cloudExporting, setCloudExporting] = useState(false);
  const [cancelingCloudJobId, setCancelingCloudJobId] = useState(null);

  const formats = [
    { id: 'wav16', label: 'WAV 16-bit', icon: 'FileAudio', ext: 'wav' },
    { id: 'wav24', label: 'WAV 24-bit', icon: 'FileAudio2', ext: 'wav' },
  ];
  const cloudFormats = [
    ...formats,
    { id: 'mp3', label: 'MP3 320k', icon: 'Headphones', ext: 'mp3' },
    { id: 'flac', label: 'FLAC', icon: 'Gem', ext: 'flac' },
  ];

  const sampleRates = [
    { id: '44100', label: '44.1 kHz' },
    { id: '48000', label: '48 kHz' },
    { id: '96000', label: '96 kHz' },
  ];

  const handleBounce = async () => {
    setIsExporting(true);
    setExportProgress(0);
    try {
      // Simulate export progress
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(r => setTimeout(r, 150));
        setExportProgress(i);
      }
      const exportResult = onBounce ? await onBounce({ format, sampleRate }) : null;
      if (!exportResult?.blob) {
        throw new Error('No export was generated.');
      }
      const selectedFmt = formats?.find(f => f?.id === format);
      const url = URL.createObjectURL(exportResult.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportResult?.fileName || `mixdown_${Date.now()}.${selectedFmt?.ext || 'wav'}`;
      a?.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const handleStemExport = async (channel) => {
    const stemKey = channel?.channelKey || channel?.id;
    const stemLabel = `${channel?.sectionLabel || 'track'}_${channel?.customName || channel?.id}`;
    setStemExporting(stemKey);
    try {
      if (!channel?.filePath) {
        throw new Error('No audio file is available for this stem.');
      }
      const a = document.createElement('a');
      a.href = channel.filePath;
      a.download = `stem_${stemLabel?.toLowerCase()?.replace(/\s/g, '_')}_${Date.now()}.webm`;
      a?.click();
    } catch (e) {
      console.error('Stem export error:', e);
    } finally {
      setStemExporting(null);
    }
  };

  const handleAllStemExport = async () => {
    const stems = (recordedChannels || []).filter((channel) => channel?.filePath);
    if (!stems.length) return;
    setStemExporting('all');
    try {
      stems.forEach((channel, index) => {
        const stemLabel = `${channel?.sectionLabel || 'track'}_${channel?.customName || channel?.id}`;
        const a = document.createElement('a');
        a.href = channel.filePath;
        a.download = `stem_${stemLabel?.toLowerCase()?.replace(/\s/g, '_')}_${Date.now()}_${index + 1}.webm`;
        a.click();
      });
    } catch (e) {
      console.error('All stems export error:', e);
    } finally {
      setStemExporting(null);
    }
  };

  const handleCloudExport = async (exportType = 'mixdown') => {
    if (!onQueueCloudExport) return;
    setCloudExporting(true);
    try {
      await onQueueCloudExport({
        export_type: exportType,
        format: cloudFormat,
        sample_rate: Number(sampleRate),
        options: {
          recorded_channels: recordedChannels?.map((channel) => ({
            channelKey: channel?.channelKey,
            sectionLabel: channel?.sectionLabel,
            id: channel?.id,
            customName: channel?.customName,
          })) || [],
        },
      });
    } catch (e) {
      console.error('Cloud export queue error:', e);
    } finally {
      setCloudExporting(false);
    }
  };

  const handleCancelCloudExport = async (job) => {
    if (!onCancelCloudExport || !job?.id) return;
    setCancelingCloudJobId(job.id);
    try {
      await onCancelCloudExport(job);
    } catch (e) {
      console.error('Cloud export cancel error:', e);
    } finally {
      setCancelingCloudJobId(null);
    }
  };

  const recordedChannels = channels?.filter(c => c?.recorded);
  const visibleCloudJobs = useMemo(() => (cloudExportJobs || []).slice(0, 4), [cloudExportJobs]);

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 border-b border-gray-700">
        <Icon name="Download" size={13} color="#22c55e" />
        <span className="text-xs font-mono text-gray-300 uppercase tracking-wider">Export & Bounce</span>
      </div>
      <div className="p-3 space-y-3">
        {/* Local Bounce Format Selection */}
        <div>
          <span className="text-[10px] font-mono text-gray-500 uppercase block mb-1.5">Local Bounce Format</span>
          <div className="grid grid-cols-2 gap-1">
            {formats?.map(f => (
              <button
                key={f?.id}
                onClick={() => setFormat(f?.id)}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded border text-[10px] font-mono transition-all ${
                  format === f?.id
                    ? 'bg-green-900/30 border-green-500 text-green-300' :'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'
                }`}
              >
                <Icon name={f?.icon} size={12} />
                <span>{f?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sample Rate */}
        <div>
          <span className="text-[10px] font-mono text-gray-500 uppercase block mb-1.5">Sample Rate</span>
          <div className="flex gap-1">
            {sampleRates?.map(sr => (
              <button
                key={sr?.id}
                onClick={() => setSampleRate(sr?.id)}
                className={`flex-1 py-1 rounded border text-[10px] font-mono transition-all ${
                  sampleRate === sr?.id
                    ? 'bg-blue-900/30 border-blue-500 text-blue-300' :'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'
                }`}
              >
                {sr?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bounce to Stereo */}
        <button
          onClick={handleBounce}
          disabled={disabled || isExporting || !beatInfo}
          className="w-full flex items-center justify-center gap-2 py-2 rounded border border-green-600 bg-green-900/20 text-green-300 hover:bg-green-900/40 transition-all text-xs font-mono font-bold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <>
              <Icon name="Loader" size={13} className="animate-spin" />
              <span>Bouncing... {exportProgress}%</span>
            </>
          ) : (
            <>
              <Icon name="Combine" size={13} />
              <span>Bounce to Stereo Mix</span>
            </>
          )}
        </button>

        <div>
          <span className="text-[10px] font-mono text-gray-500 uppercase block mb-1.5">Cloud Export Format</span>
          <div className="grid grid-cols-2 gap-1">
            {cloudFormats?.map(f => (
              <button
                key={f?.id}
                onClick={() => setCloudFormat(f?.id)}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded border text-[10px] font-mono transition-all ${
                  cloudFormat === f?.id
                    ? 'bg-cyan-900/30 border-cyan-500 text-cyan-300' :'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'
                }`}
              >
                <Icon name={f?.icon} size={12} />
                <span>{f?.label}</span>
              </button>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-gray-500">MP3 and FLAC render on the Hetzner export worker.</p>
        </div>

        <button
          onClick={() => handleCloudExport('mixdown')}
          disabled={disabled || cloudExporting || !beatInfo || !onQueueCloudExport}
          className="w-full flex items-center justify-center gap-2 py-2 rounded border border-cyan-600 bg-cyan-900/20 text-cyan-300 hover:bg-cyan-900/40 transition-all text-xs font-mono font-bold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {cloudExporting ? (
            <>
              <Icon name="Loader" size={13} className="animate-spin" />
              <span>Queueing Cloud Export...</span>
            </>
          ) : (
            <>
              <Icon name="CloudUpload" size={13} />
      <span>Queue Cloud Export</span>
            </>
          )}
        </button>
        <button
          onClick={() => handleCloudExport('stems')}
          disabled={disabled || cloudExporting || !recordedChannels?.length || !onQueueCloudExport}
          className="w-full flex items-center justify-center gap-2 py-2 rounded border border-violet-600 bg-violet-900/20 text-violet-300 hover:bg-violet-900/40 transition-all text-xs font-mono font-bold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {cloudExporting ? (
            <>
              <Icon name="Loader" size={13} className="animate-spin" />
              <span>Queueing Stem Bundle...</span>
            </>
          ) : (
            <>
              <Icon name="FolderArchive" size={13} />
              <span>Queue Cloud Stem Bundle</span>
            </>
          )}
        </button>

        {isExporting && (
          <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all"
              style={{ width: `${exportProgress}%` }}
            />
          </div>
        )}

        <div className="rounded border border-gray-700 bg-gray-950/50">
          <div className="flex items-center justify-between gap-2 border-b border-gray-800 px-2 py-1.5">
            <span className="text-[10px] font-mono text-gray-500 uppercase">Cloud Export Jobs</span>
            <button
              type="button"
              onClick={onRefreshCloudExports}
              disabled={!onRefreshCloudExports || cloudExportsLoading}
              className="flex items-center gap-1 rounded border border-gray-700 px-1.5 py-1 text-[10px] font-mono text-gray-300 hover:border-cyan-500 disabled:opacity-40"
            >
              <Icon name={cloudExportsLoading ? 'Loader' : 'RefreshCw'} size={10} className={cloudExportsLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
          {visibleCloudJobs.length ? (
            <div className="divide-y divide-gray-800">
              {visibleCloudJobs.map((job) => {
                const downloadUrl = job?.result_file?.download_url || job?.result_file?.public_url;
                const status = job?.status || 'queued';
                const canCancel = ['queued', 'processing'].includes(status);
                return (
                  <div key={job?.id} className="space-y-1 px-2 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-[10px] font-mono text-gray-300">
                        {(job?.export_type || 'mixdown').toUpperCase()} · {(job?.format || 'wav').toUpperCase()}
                      </span>
                      <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-mono uppercase ${statusStyles[status] || statusStyles.queued}`}>
                        {status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[10px] font-mono text-gray-500">
                      <span>{job?.sample_rate ? `${Number(job.sample_rate) / 1000} kHz` : '44.1 kHz'}</span>
                      {job?.result_file?.file_size ? <span>{formatFileSize(job.result_file.file_size)}</span> : null}
                    </div>
                    {status === 'completed' && downloadUrl && (
                      <a
                        href={downloadUrl}
                        download={job?.result_file?.filename || 'cloud-export.wav'}
                        className="mt-1 flex w-full items-center justify-center gap-1.5 rounded border border-green-600/60 bg-green-900/20 px-2 py-1.5 text-[10px] font-mono font-bold text-green-300 hover:bg-green-900/40"
                      >
                        <Icon name="Download" size={11} />
                        Download Cloud Export
                      </a>
                    )}
                    {canCancel && (
                      <button
                        type="button"
                        onClick={() => handleCancelCloudExport(job)}
                        disabled={!onCancelCloudExport || cancelingCloudJobId === job?.id}
                        className="mt-1 flex w-full items-center justify-center gap-1.5 rounded border border-red-600/50 bg-red-950/20 px-2 py-1.5 text-[10px] font-mono font-bold text-red-300 hover:bg-red-950/40 disabled:opacity-40"
                      >
                        <Icon name={cancelingCloudJobId === job?.id ? 'Loader' : 'XCircle'} size={11} className={cancelingCloudJobId === job?.id ? 'animate-spin' : ''} />
                        Cancel Cloud Export
                      </button>
                    )}
                    {status === 'failed' && job?.error_message && (
                      <p className="text-[10px] font-mono text-red-300">{job.error_message}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-2 py-3 text-[10px] font-mono text-gray-500">
              No cloud exports queued for this session.
            </div>
          )}
        </div>

        {/* Stem Export */}
        {recordedChannels?.length > 0 && (
          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <span className="text-[10px] font-mono text-gray-500 uppercase">Stem Export</span>
              <button
                type="button"
                onClick={handleAllStemExport}
                disabled={stemExporting !== null || !recordedChannels?.some((channel) => channel?.filePath)}
                className="flex items-center gap-1 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-[10px] font-mono font-bold text-gray-300 hover:border-green-500 hover:text-green-300 disabled:opacity-50"
              >
                {stemExporting === 'all' ? (
                  <Icon name="Loader" size={11} className="animate-spin text-green-400" />
                ) : (
                  <Icon name="FolderDown" size={11} color="#86efac" />
                )}
                All Stems
              </button>
            </div>
            <div className="space-y-1">
              {recordedChannels?.map(ch => (
                <button
                  key={ch?.channelKey || ch?.id}
                  onClick={() => handleStemExport(ch)}
                  disabled={stemExporting !== null}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded border border-gray-600 bg-gray-800 hover:border-gray-500 text-[10px] font-mono text-gray-300 hover:text-white transition-all disabled:opacity-50"
                >
                  <span>{ch?.sectionLabel ? `${ch.sectionLabel} ` : ''}{ch?.customName || ch?.id?.charAt(0)?.toUpperCase() + ch?.id?.slice(1)}</span>
                  {stemExporting === (ch?.channelKey || ch?.id) ? (
                    <Icon name="Loader" size={11} className="animate-spin text-green-400" />
                  ) : (
                    <Icon name="Download" size={11} color="#6b7280" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportPanel;
